import React, { useEffect, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FIELD_FORM = {
  name: '',
  address: '',
  city: '',
  state: '',
  turf_type: 'synthetic',
  capacity: '',
  has_lighting: true,
  has_parking: false,
  photo_url: '',
};

export function FieldsPage({ loading, error, data, onCreateField, onUpdateField, onDeleteField, onUploadFieldImage, saving, uploading, canManage }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FIELD_FORM);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageMessage, setImageMessage] = useState('');
  const items = data?.items || [];

  useEffect(() => {
    if (!editingId) {
      setForm(EMPTY_FIELD_FORM);
      setSelectedImage(null);
      setImageMessage('');
    }
  }, [editingId]);

  if (loading) return <LoadingBlock label="Carregando campos..." />;
  if (error) return <div className="page-error">{error}</div>;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      capacity: form.capacity ? Number(form.capacity) : null,
    };

    if (editingId) {
      await onUpdateField(editingId, payload);
    } else {
      await onCreateField(payload);
    }

    setEditingId(null);
    setForm(EMPTY_FIELD_FORM);
    setSelectedImage(null);
    setImageMessage('');
  };

  const uploadSelectedImage = async () => {
    if (!selectedImage || !onUploadFieldImage) return;
    const url = await onUploadFieldImage(selectedImage, editingId);
    if (url) {
      setForm((current) => ({ ...current, photo_url: url }));
      setSelectedImage(null);
      setImageMessage('Imagem enviada. Salve o campo para aplicar na agenda.');
    }
  };

  const fieldsWithLighting = items.filter((item) => Number(item.has_lighting) === 1).length;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Campos"
        subtitle="Cadastro oficial de locais usados na agenda da competição."
        actions={canManage ? (
          <div className="inline-actions">
            <button className="ghost-button" type="button" onClick={() => { setEditingId(null); setForm(EMPTY_FIELD_FORM); }}>Novo campo</button>
            {editingId ? <button className="ghost-button" type="button" onClick={() => { setEditingId(null); setForm(EMPTY_FIELD_FORM); }}>Cancelar edição</button> : null}
          </div>
        ) : null}
      >
        <div className="callout-box compact-box admin-callout">
          <span className="eyebrow">Admin</span>
          <strong>Controle total da infraestrutura</strong>
          <p>Cadastre, edite e remova os campos oficiais do campeonato. Isso organiza agenda, cidade, piso, iluminação e capacidade de operação.</p>
        </div>

        <div className="callout-grid admin-summary-grid">
          <div className="callout-box compact-box"><span className="eyebrow">Campos</span><strong>{items.length}</strong><p>Locais oficiais cadastrados.</p></div>
          <div className="callout-box compact-box"><span className="eyebrow">Com iluminação</span><strong>{fieldsWithLighting}</strong><p>Prontos para rodada noturna.</p></div>
        </div>

        {canManage ? (
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <label>
                Nome do campo
                <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
              <label>
                Endereço
                <input value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} required />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Cidade
                <input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} required />
              </label>
              <label>
                UF
                <input value={form.state} maxLength="2" onChange={(event) => setForm({ ...form, state: event.target.value.toUpperCase() })} required />
              </label>
              <label>
                Piso
                <select value={form.turf_type} onChange={(event) => setForm({ ...form, turf_type: event.target.value })}>
                  <option value="natural">Natural</option>
                  <option value="synthetic">Sintético</option>
                  <option value="indoor">Indoor</option>
                </select>
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Capacidade
                <input type="number" value={form.capacity} onChange={(event) => setForm({ ...form, capacity: event.target.value })} />
              </label>
              <label className="checkbox-field">
                <span>Iluminação</span>
                <input type="checkbox" checked={Boolean(form.has_lighting)} onChange={(event) => setForm({ ...form, has_lighting: event.target.checked })} />
              </label>
              <label className="checkbox-field">
                <span>Estacionamento</span>
                <input type="checkbox" checked={Boolean(form.has_parking)} onChange={(event) => setForm({ ...form, has_parking: event.target.checked })} />
              </label>
            </div>
            <div className="media-uploader">
              <div className="media-preview">
                {form.photo_url ? <img src={form.photo_url} alt={`Foto de ${form.name || 'campo'}`} /> : <span>Sem foto</span>}
              </div>
              <div className="media-uploader__controls">
                <label>
                  Foto do campo
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setSelectedImage(event.target.files?.[0] || null)} />
                </label>
                <div className="inline-actions">
                  <button className="ghost-button" type="button" onClick={uploadSelectedImage} disabled={!selectedImage || uploading}>
                    {uploading ? 'Enviando...' : 'Enviar foto'}
                  </button>
                  <button className="inline-button" type="button" onClick={() => { setSelectedImage(null); setForm({ ...form, photo_url: '' }); setImageMessage('Foto removida. Salve o campo para aplicar.'); }} disabled={!form.photo_url && !selectedImage}>
                    Remover foto
                  </button>
                </div>
                <small>JPG, PNG ou WEBP até 5 MB. A imagem aparece no cadastro e pode ser usada na agenda.</small>
                {imageMessage ? <small className="success-note compact">{imageMessage}</small> : null}
              </div>
            </div>
            <button className="primary-button" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : editingId ? 'Atualizar campo' : 'Criar campo'}
            </button>
          </form>
        ) : <div className="empty-state">Seu perfil pode consultar, mas não gerenciar campos.</div>}
      </SectionCard>

      <SectionCard title="Lista de campos" subtitle="Ações rápidas de edição e remoção.">
        <DataTable
          rows={items}
          columns={[
            { key: 'name', label: 'Campo' },
            { key: 'city', label: 'Cidade' },
            { key: 'state', label: 'UF' },
            { key: 'turf_type', label: 'Piso' },
            { key: 'capacity', label: 'Capacidade' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => canManage ? (
                <div className="inline-actions">
                  <button className="inline-button" type="button" onClick={() => {
                    setEditingId(row.id);
                    setSelectedImage(null);
                    setImageMessage('');
                    setForm({
                      name: row.name || '',
                      address: row.address || '',
                      city: row.city || '',
                      state: row.state || '',
                      turf_type: row.turf_type || 'synthetic',
                      capacity: row.capacity || '',
                      has_lighting: Boolean(row.has_lighting),
                      has_parking: Boolean(row.has_parking),
                      photo_url: row.photo_url || '',
                    });
                  }}>Editar</button>
                  <button className="inline-button danger-text" type="button" onClick={() => onDeleteField(row.id)}>Remover</button>
                </div>
              ) : 'Somente leitura',
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}
